import { useQuery } from "@tanstack/react-query";
import {
  Filter,
  Globe,
  Info,
  MessageCircleQuestion,
  QrCode,
  Rocket,
} from "lucide-react";
import { useGetList } from "ra-core";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { fetchSnapFunnel, type SnapFunnel } from "../guerilla/guerillaClient";
import {
  fetchLaunchpadFunnel,
  type LaunchpadCampaignFunnel,
  type LaunchpadFunnel,
} from "../launchpad/launchpadClient";
import { dashboardRequest } from "../providers/aws/client";
import type { TrackingLinkDailyMetric } from "../types";
import { DateRangePicker } from "../filters/DateRangePicker";
import {
  formatRangeLabel,
  preset,
  serializeRange,
  spansPartialWeeks,
  toCohortWeeks,
  toPresetToken,
  toDayWindow,
  type DateRange,
} from "../filters/dateRange";
import { FunnelBoardView } from "./FunnelBoardView";
import { FunnelKpiCards } from "./FunnelKpiCards";
import {
  ButtonGroup,
  EmptyState,
  FunnelStageBar,
  IncompleteCohortAlert,
} from "./FunnelPrimitives";
import { TrackingLinkOperationalFunnel } from "./TrackingLinkOperationalFunnel";
import {
  type FunnelLegSummary,
  type FunnelSummary,
  type MergedFunnelStage,
  FUNNEL_CONTINENTS,
  aggregateTrackingLinkActivity,
  continentLabel,
  formatInt,
  formatPct,
  legLabel,
  mergeLaunchpadPreStage,
  mergeSnapPreStage,
  ratePct,
  stageContactsHref,
} from "./funnelSummary";

const RANGE_PRESETS = [
  { value: "this_week", label: "This week" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "180d", label: "6 months" },
  { value: "all", label: "All time" },
] as const;

// The funnel aggregate is bucketed by ISO week, so a custom window is served by
// the weeks its endpoints fall in — wider than the dates asked for whenever
// either end lands mid-week. Said out loud rather than rounded silently.
const PARTIAL_WEEK_NOTE =
  "Cohorts are whole ISO weeks, so a window that starts or ends mid-week is widened to cover them.";

// Interview "stuck at question N" buckets (RC-3); mirrors the API iq__ histogram.
const IQ_BUCKETS = [
  { key: "q0", label: "Pre-Q1" },
  { key: "q1", label: "Q1" },
  { key: "q2", label: "Q2" },
  { key: "q3", label: "Q3" },
  { key: "q4", label: "Q4" },
  { key: "q5plus", label: "Q5+" },
] as const;

const LEGS = [
  { value: "all", label: "All sources" },
  { value: "website", label: "Website" },
  { value: "ctwa", label: "Meta → WhatsApp" },
  { value: "organic", label: "Organic" },
  { value: "snap", label: "Snap" },
  { value: "link", label: "Tracking links" },
  { value: "launchpad", label: "Launchpad" },
  // Web Create (MYC-015): creations on my.echoflicks.com, no WhatsApp.
  { value: "my_web", label: "My Echoflicks web" },
] as const;

const TRAFFIC_GROUPS = [
  { value: "all", label: "All traffic" },
  { value: "paid", label: "Paid" },
  { value: "organic", label: "Organic" },
  { value: "unknown", label: "Unknown" },
] as const;

export function FunnelAnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "summary" ? "summary" : "board";
  const [range, setRange] = useState<DateRange>(() => preset("this_week"));
  const rangeKey = serializeRange(range);
  const cohortWeeks = useMemo(() => toCohortWeeks(range), [range]);
  // Snap and Launchpad pre-WhatsApp funnels come from their own services, which
  // only accept the preset tokens. Rather than let them silently answer a
  // different question than the main funnel, a custom window asks them for
  // everything — always a superset — and the panels say so.
  const subServiceRange = toPresetToken(range);
  const [leg, setLeg] = useState<string>("all");
  const [acquisitionGroup, setAcquisitionGroup] = useState<string>("all");
  // Continents are an include-list; we track the EXCLUDED ones (all shown by default).
  const [excludedContinents, setExcludedContinents] = useState<string[]>([]);
  const { from: trackingLinkFrom, to: trackingLinkTo } = useMemo(
    () => toDayWindow(range),
    [range],
  );

  const {
    data: trackingLinkRows,
    isPending: trackingLinksPending,
    error: trackingLinksError,
  } = useGetList<TrackingLinkDailyMetric>(
    "tracking_link_daily_metrics",
    {
      pagination: { perPage: 2000, page: 1 },
      sort: { field: "period", order: "DESC" },
      filter: {
        ...(trackingLinkFrom ? { "period@gte": trackingLinkFrom } : {}),
        ...(trackingLinkTo ? { "period@lte": trackingLinkTo } : {}),
      },
    },
    { enabled: leg === "all" || leg === "link" },
  );
  const trackingLinkSummary = useMemo(
    () => aggregateTrackingLinkActivity(trackingLinkRows ?? []),
    [trackingLinkRows],
  );

  const setView = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "summary") params.set("view", "summary");
        else params.delete("view");
        return params;
      },
      { replace: true },
    );
  };

  const { data, isPending, error } = useQuery({
    queryKey: [
      "funnel-summary",
      rangeKey,
      leg,
      acquisitionGroup,
      [...excludedContinents].sort().join(","),
    ],
    enabled: view === "summary",
    queryFn: () => {
      // `cohort_from`/`cohort_to` override `range` server-side, so a custom
      // window sends the weeks and a preset keeps sending the token verbatim.
      const params = new URLSearchParams(
        cohortWeeks ?? { range: (range as { preset: string }).preset },
      );
      if (leg !== "all") params.set("leg", leg);
      if (acquisitionGroup !== "all")
        params.set("acquisition_group", acquisitionGroup);
      if (excludedContinents.length) {
        const included = FUNNEL_CONTINENTS.map((c) => c.code).filter(
          (c) => !excludedContinents.includes(c),
        );
        params.set("continent", included.join(","));
      }
      return dashboardRequest<{ data: FunnelSummary }>(
        `/funnel/summary?${params.toString()}`,
      ).then((res) => res.data);
    },
  });

  // Snap-only: the token-keyed pre-WhatsApp funnel from the bot. Fetched with
  // the SAME range so the two windows align. Degrades gracefully — on error the
  // pre-WhatsApp bar/banner simply don't render (mirrors the Guerilla tab).
  const { data: snapFunnel } = useQuery({
    queryKey: ["snap-funnel", subServiceRange, acquisitionGroup],
    enabled:
      view === "summary" &&
      leg === "snap" &&
      (acquisitionGroup === "all" || acquisitionGroup === "organic"),
    queryFn: () => fetchSnapFunnel(subServiceRange),
    retry: false,
  });
  const snap: SnapFunnel | null = leg === "snap" ? (snapFunnel ?? null) : null;

  // Launchpad-only: the token-keyed pre-WhatsApp funnel from the bot (web
  // sessions + finalized uploads). Fetched with the SAME range so the two
  // windows align. Degrades gracefully — on error the pre-WhatsApp bars /
  // banner / by-campaign table simply don't render (mirrors the snap leg).
  const { data: launchpadFunnel } = useQuery({
    queryKey: ["launchpad-funnel", subServiceRange, acquisitionGroup],
    enabled:
      view === "summary" &&
      leg === "launchpad" &&
      (acquisitionGroup === "all" || acquisitionGroup === "unknown"),
    queryFn: () => fetchLaunchpadFunnel(subServiceRange),
    retry: false,
  });
  const launchpad: LaunchpadFunnel | null =
    leg === "launchpad" ? (launchpadFunnel ?? null) : null;

  const toggleContinent = (code: string) =>
    setExcludedContinents((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Filter className="size-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Onboarding funnel</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Where B2C contacts get stuck on the way to a rated Echo, by arrival
            cohort and acquisition source. Real-estate (B2B) is excluded.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(next) => next && setView(next)}
            variant="outline"
            size="sm"
          >
            <ToggleGroupItem value="board">Board</ToggleGroupItem>
            <ToggleGroupItem value="summary">Summary</ToggleGroupItem>
          </ToggleGroup>
          <DateRangePicker
            label="Cohort"
            presets={RANGE_PRESETS}
            value={range}
            onChange={setRange}
            note={PARTIAL_WEEK_NOTE}
          />
          <ButtonGroup
            label="Traffic"
            options={TRAFFIC_GROUPS}
            value={acquisitionGroup}
            onChange={setAcquisitionGroup}
          />
          <ButtonGroup
            label="Source"
            options={LEGS}
            value={leg}
            onChange={setLeg}
          />
        </div>
      </div>

      {view === "summary" && (
        <ContinentFilter
          excluded={excludedContinents}
          onToggle={toggleContinent}
        />
      )}

      {range.kind === "custom" && (
        <Alert>
          <Info className="size-4" />
          <AlertTitle>Showing {formatRangeLabel(range)}</AlertTitle>
          <AlertDescription>
            <p>
              {spansPartialWeeks(range)
                ? `${PARTIAL_WEEK_NOTE} These cohorts run ${cohortWeeks?.cohort_from} to ${cohortWeeks?.cohort_to}.`
                : `These cohorts run ${cohortWeeks?.cohort_from} to ${cohortWeeks?.cohort_to}.`}
              {(leg === "snap" || leg === "launchpad") &&
                " The pre-WhatsApp panel below comes from a separate service that has no custom-range support, so it shows all time."}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {leg === "snap" && snap && (
        <Alert>
          <QrCode className="size-4" />
          <AlertTitle>
            Scan tracking since {formatTrackingSince(snap.tracking_since)}
          </AlertTitle>
          <AlertDescription>
            The <strong>QR scanned</strong> stage is sourced from booth QR scans
            (no contact record), stitched onto the front of the WhatsApp funnel.
            {snap.incomplete_cohort ? (
              <>
                {" "}
                Recent scans may not have messaged yet — the &ldquo;scanned but
                never messaged&rdquo; count uses a 72&nbsp;h conversion window.
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      )}

      {leg === "launchpad" && launchpad && (
        <Alert>
          <Rocket className="size-4" />
          <AlertTitle>
            Launchpad tracking since{" "}
            {formatTrackingSince(launchpad.tracking_since)}
          </AlertTitle>
          <AlertDescription>
            The <strong>Launchpad started</strong> and{" "}
            <strong>Uploads finalized</strong> stages are sourced from launchpad
            web sessions (no contact record), stitched onto the front of the
            WhatsApp funnel.
            {launchpad.incomplete_cohort ? (
              <>
                {" "}
                Recent sessions may not have messaged yet — the &ldquo;started
                but never messaged&rdquo; count uses a{" "}
                {launchpad.params.window_hours ?? 72}&nbsp;h conversion window.
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      )}

      {view === "summary" && data?.incomplete_cohort && (
        <IncompleteCohortAlert />
      )}

      <Alert>
        <Info className="size-4" />
        <AlertTitle>How to read this</AlertTitle>
        <AlertDescription>
          Each contact is counted at the <strong>furthest stage</strong> they
          reached. <strong>Marketing opt-in</strong> = answered the opt-in
          question (yes or no); people who skipped it and ran{" "}
          <code>/create</code> still count as having passed it, so{" "}
          <strong>Stuck here</strong> on Privacy acknowledged = never answered.{" "}
          {leg === "snap" ? (
            <>
              For Snap, people who{" "}
              <strong>scanned the QR but never messaged</strong> now show in the{" "}
              <strong>QR scanned</strong> bar at the top of the funnel (sourced
              from scans — they have no contact record).
            </>
          ) : leg === "launchpad" ? (
            <>
              For Launchpad, people who{" "}
              <strong>started on the web but never messaged</strong> show in the{" "}
              <strong>Launchpad started</strong> and{" "}
              <strong>Uploads finalized</strong> bars at the top of the funnel
              (sourced from web sessions — they have no contact record).
            </>
          ) : (
            <>
              First-touch cohorts below keep a returning contact&apos;s original
              acquisition source. Exact tracking-link clicks and attributed
              Echoes appear in the operational funnel above.
            </>
          )}{" "}
          After a 2nd delivered Echo a contact{" "}
          <strong>graduates to recurring</strong> and leaves the funnel. Cohorts
          are bucketed by ISO week. Echo outcome cards are workload-level, so a
          later successful Echo never hides an earlier failed one.
        </AlertDescription>
      </Alert>

      {(leg === "all" || leg === "link") &&
        (acquisitionGroup === "all" || acquisitionGroup === "unknown") && (
          <TrackingLinkOperationalFunnel
            summary={trackingLinkSummary}
            isPending={trackingLinksPending}
            error={trackingLinksError}
            geographyFiltered={excludedContinents.length > 0}
          />
        )}

      {view === "board" ? (
        <FunnelBoardView
          range={range}
          leg={leg}
          acquisitionGroup={acquisitionGroup}
        />
      ) : error ? (
        <EmptyState
          text={`Could not load the funnel: ${(error as Error).message}`}
        />
      ) : isPending ? (
        <EmptyState text="Loading…" />
      ) : !data ||
        (data.cohort_size === 0 &&
          data.echo_outcomes.attempted_echoes === 0) ? (
        <EmptyState text="No contacts arrived in this cohort yet." />
      ) : (
        <FunnelBody data={data} snap={snap} launchpad={launchpad} />
      )}
    </div>
  );
}

FunnelAnalyticsPage.path = "/funnel";

function formatTrackingSince(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function FunnelBody({
  data,
  snap,
  launchpad,
}: {
  data: FunnelSummary;
  snap: SnapFunnel | null;
  launchpad: LaunchpadFunnel | null;
}) {
  // Prepend the bot's pre-contact "QR scanned" bar when snap data is present.
  const { stages: snapStages, reconciliation } = mergeSnapPreStage(
    data.stages,
    snap,
  );
  // Prepend the launchpad's pre-contact "Launchpad started" / "Uploads
  // finalized" bars when launchpad data is present (each merge passes stages
  // through untouched when its source is null, so at most one applies).
  const { stages: mergedStages, reconciliation: launchpadReconciliation } =
    mergeLaunchpadPreStage(snapStages, launchpad);
  // Pre-contact bars are token-keyed bot data, not CONTACT# rows; the tag and
  // tooltip say where they came from (QR scans vs launchpad web sessions).
  const preContactTag = launchpad ? "web" : "QR";
  const preContactNote = launchpad
    ? "sourced from launchpad web sessions — no contact record"
    : "sourced from QR scans — no contact record";
  return (
    <>
      <FunnelKpiCards data={data} />

      <Card>
        <CardHeader>
          <CardTitle>Conversion funnel</CardTitle>
          <p className="text-xs text-muted-foreground">
            Contacts who reached each stage (cumulative). Click a stage to see
            the contacts whose furthest stage is exactly there.
          </p>
        </CardHeader>
        <CardContent>
          {/* Pre-contact "QR scanned" bar is greyed to read as a distinct,
              non-contact source; contact stages keep the funnel teal. */}
          <FunnelStageBar
            stages={mergedStages.map((s) => ({
              label: s.pre_contact ? `${s.label} (${preContactTag})` : s.label,
              reached: s.reached,
              muted: Boolean(s.pre_contact),
              note: s.pre_contact ? preContactNote : undefined,
            }))}
          />
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Reached</TableHead>
                  <TableHead className="text-right">% of cohort</TableHead>
                  <TableHead className="text-right">Step conv.</TableHead>
                  <TableHead className="text-right">Stuck here</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mergedStages.map((s: MergedFunnelStage) => (
                  <TableRow key={s.key}>
                    <TableCell className="font-medium">
                      {s.pre_contact ? (
                        <span
                          className="text-muted-foreground"
                          title={preContactNote}
                        >
                          {s.label}{" "}
                          <span className="text-xs">({preContactTag})</span>
                        </span>
                      ) : (
                        s.label
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInt(s.reached)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.pre_contact ? "—" : formatPct(s.pct_of_cohort)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.step_conversion_pct == null
                        ? "—"
                        : formatPct(s.step_conversion_pct)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.pre_contact ? (
                        // Pre-contact: no CONTACT# rows to drill into.
                        formatInt(s.at_stage)
                      ) : s.at_stage > 0 ? (
                        <Link
                          to={stageContactsHref(s.key)}
                          className="text-primary hover:underline"
                        >
                          {formatInt(s.at_stage)}
                        </Link>
                      ) : (
                        formatInt(s.at_stage)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {reconciliation ? (
            <p className="mt-2 text-xs text-muted-foreground">
              QR-side messaged: {formatInt(reconciliation.qr_side_messaged)} /
              funnel: {formatInt(reconciliation.funnel_messaged)} — the QR
              funnel counts codes claimed on the bot; the WhatsApp funnel
              excludes test numbers, so a small delta is expected.
            </p>
          ) : null}
          {launchpadReconciliation ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Launchpad-side messaged:{" "}
              {formatInt(launchpadReconciliation.launchpad_side_messaged)} /
              funnel: {formatInt(launchpadReconciliation.funnel_messaged)} — the
              launchpad funnel counts EFL codes claimed on the bot; the WhatsApp
              funnel excludes test numbers, so a small delta is expected.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {launchpad && launchpad.by_campaign.length > 0 ? (
        <LaunchpadByCampaignCard byCampaign={launchpad.by_campaign} />
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircleQuestion className="size-4 text-muted-foreground" />
              Interview drop-off
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Of those who entered the Felix interview, how many reached a
              delivered Echo.
            </p>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label="Entered interview"
              value={data.interview_dropoff.entered}
            />
            <Row
              label="Reached delivery"
              value={data.interview_dropoff.completed}
            />
            <Row
              label="Stuck in interview"
              value={data.interview_dropoff.stuck}
              emphasize
            />
            <div className="pt-2">
              <p className="mb-1 text-xs text-muted-foreground">
                Stuck at question
                {data.interview_dropoff.avg_asked != null
                  ? ` · avg ${data.interview_dropoff.avg_asked} asked / ${data.interview_dropoff.avg_answered} answered`
                  : ""}
              </p>
              {data.interview_dropoff.stuck > 0 ? (
                <div className="space-y-1">
                  {IQ_BUCKETS.map(({ key, label }) => {
                    const n = data.interview_dropoff.stuck_histogram[key] ?? 0;
                    const pct = data.interview_dropoff.stuck
                      ? Math.round((100 * n) / data.interview_dropoff.stuck)
                      : 0;
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span className="w-12 shrink-0 text-muted-foreground">
                          {label}
                        </span>
                        <div className="h-2 flex-1 rounded bg-muted">
                          <div
                            className="h-2 rounded bg-primary"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-8 shrink-0 text-right tabular-nums">
                          {formatInt(n)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No interview data yet.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By source</CardTitle>
            <p className="text-xs text-muted-foreground">
              Same funnel per acquisition channel — spot a source that stalls at
              a stage.
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Cohort</TableHead>
                    <TableHead className="text-right">Rated</TableHead>
                    <TableHead className="text-right">Conv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(data.by_leg).map(([legKey, summary]) => (
                    <LegRow key={legKey} leg={legKey} summary={summary} />
                  ))}
                  {!Object.keys(data.by_leg).length && (
                    <TableRow>
                      <TableCell colSpan={4} className="h-16 text-center">
                        No source breakdown in this cohort.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="size-4 text-muted-foreground" />
            By continent
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Same funnel per region (by the contact&apos;s phone country). Use
            the Region filter above to include or exclude continents.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Continent</TableHead>
                  <TableHead className="text-right">Cohort</TableHead>
                  <TableHead className="text-right">/create</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Rated</TableHead>
                  <TableHead className="text-right">Conv.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(data.by_continent)
                  .sort((a, b) => b[1].cohort_size - a[1].cohort_size)
                  .map(([code, summary]) => (
                    <ContinentRow key={code} code={code} summary={summary} />
                  ))}
                {!Object.keys(data.by_continent).length && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-16 text-center">
                      No region breakdown in this cohort.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/**
 * Launchpad-only: the bot's per-utm_campaign slice of the pre-WhatsApp funnel.
 * Rendered below the stage table so operators can compare Meta campaigns
 * without leaving the tab. Rows keep the bot's delivered order.
 */
function LaunchpadByCampaignCard({
  byCampaign,
}: {
  byCampaign: LaunchpadCampaignFunnel[];
}) {
  const stageCount = (row: LaunchpadCampaignFunnel, key: string) =>
    row.stages.find((s) => s.key === key)?.count ?? 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>By campaign</CardTitle>
        <p className="text-xs text-muted-foreground">
          Launchpad sessions per utm_campaign (sourced from the bot&apos;s
          token-keyed funnel). &ldquo;(none)&rdquo; groups sessions that arrived
          without a campaign tag. Drop-off = started but never messaged
          WhatsApp.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Started</TableHead>
                <TableHead className="text-right">Uploads finalized</TableHead>
                <TableHead className="text-right">Messaged</TableHead>
                <TableHead className="text-right">Echoes</TableHead>
                <TableHead className="text-right">Drop-off</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byCampaign.map((row) => (
                <TableRow key={row.campaign}>
                  <TableCell className="font-medium">{row.campaign}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(stageCount(row, "session_started"))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(stageCount(row, "media_ready"))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(stageCount(row, "conversation_started"))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(stageCount(row, "echo_delivered"))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInt(row.started_not_messaged)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ContinentRow({
  code,
  summary,
}: {
  code: string;
  summary: FunnelLegSummary;
}) {
  const stage = (key: string) =>
    summary.stages.find((s) => s.key === key)?.reached ?? 0;
  const rated = stage("rated");
  const emoji = FUNNEL_CONTINENTS.find((c) => c.code === code)?.emoji ?? "🌐";
  return (
    <TableRow>
      <TableCell className="font-medium">
        {emoji} {continentLabel(code)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(summary.cohort_size)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(stage("create_invoked"))}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(stage("echo_delivered"))}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(rated)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatPct(ratePct(rated, summary.cohort_size))}
      </TableCell>
    </TableRow>
  );
}

function ContinentFilter({
  excluded,
  onToggle,
}: {
  excluded: string[];
  onToggle: (code: string) => void;
}) {
  const shown = FUNNEL_CONTINENTS.filter((c) =>
    ["AF", "EU", "AS", "NA", "SA", "OC", "UNKNOWN"].includes(c.code),
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Globe className="size-3.5" /> Region
      </span>
      {shown.map((c) => {
        const active = !excluded.includes(c.code);
        return (
          <Button
            key={c.code}
            type="button"
            size="sm"
            variant={active ? "secondary" : "ghost"}
            className={active ? "" : "text-muted-foreground/60 line-through"}
            onClick={() => onToggle(c.code)}
          >
            {c.emoji} {c.label}
          </Button>
        );
      })}
    </div>
  );
}

function LegRow({ leg, summary }: { leg: string; summary: FunnelLegSummary }) {
  const rated = summary.stages.find((s) => s.key === "rated")?.reached ?? 0;
  return (
    <TableRow>
      <TableCell>
        <Badge variant="outline">{legLabel(leg)}</Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(summary.cohort_size)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatInt(rated)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatPct(ratePct(rated, summary.cohort_size))}
      </TableCell>
    </TableRow>
  );
}

function Row({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: number;
  emphasize?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`tabular-nums ${emphasize ? "font-semibold" : "font-medium"}`}
      >
        {formatInt(value)}
      </span>
    </div>
  );
}
